/**
 * NuGuard AI - Login Component
 * 
 * Authentication page with new design styling.
 * Maintains existing auth logic with authApi integration.
 */

import React, { useRef, useState } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Lock, Mail, ArrowRight, Loader2, Eye, EyeOff, User as UserIcon, Building2 } from 'lucide-react';
import { authApi } from '../services/api';
import { CONFIG } from '../config';
import { User } from '../types';
import { adaptUserResponse } from '../adapters';
import { cn } from '../lib/utils';
import logoImg from '../assets/logo.png';

interface LoginProps {
  onLogin: (user: User) => void;
  /** Destination to restore after OAuth redirect (saved to sessionStorage). */
  postLoginRedirect?: string;
  /** Open in a specific mode instead of reading from ?mode= URL param. */
  initialMode?: 'login' | 'signup' | 'forgot-password' | 'reset-password';
}

const DEMO_ADMIN_EMAIL = 'admin@nuguard.ai';
const DEMO_ADMIN_PASSWORD = 'admin123';
const DEMO_EDITOR_EMAIL = 'editor@nuguard.ai';
const DEMO_EDITOR_PASSWORD = 'editor123';
const DEMO_VIEWER_EMAIL = 'viewer@nuguard.ai';
const DEMO_VIEWER_PASSWORD = 'viewer123';

/** Resend verification email action for the verification pending screen. */
const ResendVerificationButton: React.FC<{ email: string }> = ({ email }) => {
  const [sent, setSent] = React.useState(false);
  const [loading, setLoading] = React.useState(false);

  const handleResend = async () => {
    setLoading(true);
    try {
      await authApi.resendVerification(email);
    } finally {
      setLoading(false);
      setSent(true);
    }
  };

  if (sent) {
    return (
      <p className="mt-4 text-xs text-emerald-600 dark:text-emerald-400">
        Verification email resent — check your inbox.
      </p>
    );
  }

  return (
    <button
      type="button"
      disabled={loading}
      onClick={handleResend}
      className="w-full rounded-lg bg-primary-600 px-4 py-2.5 text-sm font-medium text-white transition-colors hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 dark:focus:ring-offset-nuguard-card"
    >
      {loading ? 'Sending…' : 'Resend verification email'}
    </button>
  );
};

const Login: React.FC<LoginProps> = ({ onLogin, postLoginRedirect, initialMode: initialModeProp }) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const inviteEmail = searchParams.get('email') || '';
  const inviteOrg = searchParams.get('org') || '';
  const isInvite = !!inviteEmail && searchParams.get('mode') !== 'reset-password';
  const resetToken = searchParams.get('token') || '';
  const isReset = searchParams.get('mode') === 'reset-password' && !!resetToken;

  const initialMode = (): 'login' | 'signup' | 'forgot-password' | 'reset-password' => {
    if (isReset) return 'reset-password';
    if (initialModeProp) return initialModeProp;
    if (searchParams.get('mode') === 'signup') return 'signup';
    return 'login';
  };

  const [mode, setMode] = useState<'login' | 'signup' | 'forgot-password' | 'reset-password'>(initialMode);
  const [pendingEmail, setPendingEmail] = useState<string | null>(null);
  const [resetDone, setResetDone] = useState(false);
  const [email, setEmail] = useState(
    isReset ? (searchParams.get('email') || '') :
    isInvite ? inviteEmail :
    ''
  );
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [name, setName] = useState('');
  const [orgName, setOrgName] = useState(
    isReset ? (searchParams.get('org') || '') : inviteOrg
  );
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [emailTouched, setEmailTouched] = useState(false);
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [nameTouched, setNameTouched] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const emailInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);

  const switchMode = (next: 'login' | 'signup' | 'forgot-password' | 'reset-password') => {
    setMode(next);
    setError('');
    setSubmitted(false);
    setEmailTouched(false);
    setPasswordTouched(false);
    setNameTouched(false);
    setResetDone(false);
    setPassword('');
    setConfirmPassword('');
    if (next === 'login') {
      if (isInvite) {
        setEmail(inviteEmail);
      } else {
        setEmail('');
      }
    } else if (next === 'signup') {
      setEmail(isInvite ? inviteEmail : '');
    } else if (next === 'forgot-password') {
      setEmail('');
    }
  };

  const normalizedEmail = email.trim();
  const emailError = !normalizedEmail
    ? 'Email is required.'
    : !/^\S+@\S+\.\S+$/.test(normalizedEmail)
      ? 'Enter a valid email address.'
      : '';
  const passwordError = (mode === 'login' || mode === 'signup' || mode === 'reset-password') && (!password.trim()
    ? 'Password is required.'
    : password.length < 6
      ? 'Password must be at least 6 characters.'
      : '');
  const confirmPasswordError = mode === 'reset-password' && confirmPassword !== password
    ? 'Passwords do not match.'
    : '';
  const nameError = mode === 'signup' && !name.trim() ? 'Full name is required.' : '';
  const isFormValid = !emailError && !passwordError && !confirmPasswordError && !nameError;
  const showEmailError = (emailTouched || submitted) && !!emailError;
  const showPasswordError = (passwordTouched || submitted) && !!passwordError;
  const showNameError = (nameTouched || submitted) && !!nameError;

  const finishLogin = async () => {
    const currentUser = await authApi.getCurrentUser();
    onLogin(adaptUserResponse(currentUser));
  };

  const loginWithDemo = async (demoEmail: string, demoPassword: string) => {
    setEmail(demoEmail);
    setPassword(demoPassword);
    setError('');
    setLoading(true);
    try {
      await authApi.login({ email: demoEmail, password: demoPassword });
      await finishLogin();
    } catch (err) {
      // Login is a special case: backend detail (e.g. "Invalid credentials", "Account disabled")
      // IS the user-facing message. Do NOT use getErrorMessage here — its 401→"Session expired"
      // translation is wrong for users who are actively trying to authenticate.
      setError(err instanceof Error ? err.message : 'Demo login failed');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSubmitted(true);
    setEmailTouched(true);
    setPasswordTouched(true);
    if (mode === 'signup') setNameTouched(true);

    if (nameError) { nameInputRef.current?.focus(); return; }
    if (emailError) { emailInputRef.current?.focus(); return; }
    if (passwordError || confirmPasswordError) { passwordInputRef.current?.focus(); return; }

    setError('');
    setLoading(true);
    try {
      if (mode === 'forgot-password') {
        await authApi.forgotPassword(normalizedEmail);
        setPendingEmail(normalizedEmail);
        return;
      }

      if (mode === 'reset-password') {
        await authApi.resetPassword(resetToken, password);
        setResetDone(true);
        return;
      }

      if (mode === 'signup') {
        try {
          const result = await authApi.signup({
            name: name.trim(),
            email: normalizedEmail,
            password,
            ...(orgName.trim() ? { org_name: orgName.trim() } : {}),
          });
          if ('status' in result && result.status === 'pending_verification') {
            setPendingEmail(result.email);
            return;
          }
        } catch (signupErr) {
          // If the invited user already has an account (409 conflict), switch to
          // login mode so they can log in and accept via the InvitationBanner.
          if (isInvite && signupErr instanceof Error && 'status' in signupErr && (signupErr as { status: number }).status === 409) {
            setMode('login');
            setError('You already have an account. Please sign in to accept the invitation.');
            setLoading(false);
            return;
          }
          throw signupErr;
        }
      } else {
        try {
          await authApi.login({ email: normalizedEmail, password });
        } catch (loginErr) {
          // If the account exists but email is not yet verified, show the
          // verification-pending screen so the user can resend the link.
          if (
            loginErr instanceof Error &&
            loginErr.message === 'Email not verified'
          ) {
            setPendingEmail(normalizedEmail);
            setLoading(false);
            return;
          }
          throw loginErr;
        }
      }
      await finishLogin();
    } catch (err) {
      // See note in loginWithDemo: keep raw backend message for login failures.
      setError(err instanceof Error ? err.message : (mode === 'signup' ? 'Sign up failed' : mode === 'reset-password' ? 'Password reset failed' : 'Invalid credentials'));
    } finally {
      setLoading(false);
    }
  };

  const logoHeader = (
    <div className="flex flex-col items-center mb-6">
      <img src={logoImg} alt="NuGuard AI" className="w-8 h-8 object-contain mb-3" />
      <h1 className="ng-page-title-xl tracking-tight">NuGuard AI</h1>
      <p className="ng-page-subtitle">AI Trust Platform</p>
    </div>
  );

  if (pendingEmail) {
    const isForgot = mode === 'forgot-password';
    const isVerification = !isForgot;
    return (
      <div className="ng-auth-shell">
        <div className="w-full max-w-md p-8">
          {logoHeader}
          <div className="ng-auth-card text-center">
            <div className="w-14 h-14 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center mx-auto mb-4">
              <Mail className="w-7 h-7 text-primary-600 dark:text-primary-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Check your email</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">
              {isForgot ? 'If that address is registered, we sent a reset link to' : 'We sent a verification link to'}
            </p>
            <p className="text-sm font-medium text-gray-800 dark:text-gray-200 mb-6">{pendingEmail}</p>
            <p className="text-xs text-gray-400 dark:text-gray-500">
              {isForgot
                ? 'Click the link in the email to reset your password. The link expires in 1 hour.'
                : 'Click the link in the email to activate your account. The link expires in 24 hours.'}
            </p>
            <div className="mt-6 space-y-3">
              {isVerification && (
                <ResendVerificationButton email={pendingEmail} />
              )}
              <button
                type="button"
                onClick={() => { setPendingEmail(null); switchMode('login'); }}
                className="w-full rounded-lg border border-primary-200 px-4 py-2.5 text-sm font-medium text-primary-700 transition-colors hover:bg-primary-50 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 dark:border-primary-700 dark:text-primary-300 dark:hover:bg-primary-900/20 dark:focus:ring-offset-nuguard-card"
              >
                Return to home
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (resetDone) {
    return (
      <div className="ng-auth-shell">
        <div className="w-full max-w-md p-8">
          {logoHeader}
          <div className="ng-auth-card text-center">
            <div className="w-14 h-14 rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center mx-auto mb-4">
              <Lock className="w-7 h-7 text-emerald-600 dark:text-emerald-400" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Password updated</h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
              Your password has been reset successfully. You can now sign in with your new password.
            </p>
            <button
              type="button"
              onClick={() => {
                const resetEmail = email;
                setResetDone(false);
                setMode('login');
                setEmail(resetEmail);
                setPassword('');
                setConfirmPassword('');
                setError('');
                navigate('/login', { replace: true });
              }}
              className="mt-2 text-sm text-primary-600 dark:text-primary-400 hover:underline"
            >
              Back to sign in
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="ng-auth-shell">
      <div className="w-full max-w-md p-8">
        {logoHeader}

        {/* Login Card */}
        <div className="ng-auth-card">
          {/* Sign In / Sign Up tabs — hidden when arriving via invite or reset link */}
          {!isInvite && !isReset && mode !== 'forgot-password' && (
            <div className="flex rounded-xl bg-stone-100 dark:bg-nuguard-card p-1 mb-4">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className={cn(
                  'flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                  mode === 'login'
                    ? 'bg-white dark:bg-nuguard-edge text-stone-900 dark:text-white shadow-sm'
                    : 'text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200'
                )}
              >
                Sign In
              </button>
              <button
                type="button"
                onClick={() => switchMode('signup')}
                className={cn(
                  'flex-1 py-2 text-sm font-medium rounded-lg transition-all duration-200',
                  mode === 'signup'
                    ? 'bg-white dark:bg-nuguard-edge text-stone-900 dark:text-white shadow-sm'
                    : 'text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200'
                )}
              >
                Sign Up
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            {/* Name Field — signup only */}
            {mode === 'signup' && (
              <div>
                <label htmlFor="name" className="ng-auth-form-label">Full Name</label>
                <div className="relative">
                  <div className="ng-input-icon-left">
                    <UserIcon className="h-5 w-5 text-stone-400 dark:text-slate-400" />
                  </div>
                  <input
                    id="name"
                    ref={nameInputRef}
                    type="text"
                    required
                    autoComplete="name"
                    className={cn(
                      "ng-auth-input pl-10 pr-3 py-2",
                      showNameError ? 'border-rose-400 dark:border-rose-500' : 'border-stone-200 dark:border-nuguard-edge'
                    )}
                    placeholder="Jane Smith"
                    value={name}
                    onBlur={() => setNameTouched(true)}
                    onChange={(e) => { setName(e.target.value); if (error) setError(''); }}
                  />
                </div>
                {showNameError && (
                  <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{nameError}</p>
                )}
              </div>
            )}

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="ng-auth-form-label">
                Work Email
              </label>
              <div className="relative">
                <div className="ng-input-icon-left">
                  <Mail className="h-5 w-5 text-stone-400 dark:text-slate-400" />
                </div>
                <input
                  id="email"
                  ref={emailInputRef}
                  type="email"
                  required
                  autoComplete="email"
                  aria-invalid={showEmailError}
                  aria-describedby={showEmailError ? 'email-error' : undefined}
                  readOnly={isInvite || isReset}
                  className={cn(
                    "ng-auth-input pl-10 pr-3 py-2",
                    (isInvite || isReset)
                      ? 'bg-stone-50 dark:bg-white/[0.04] text-stone-500 dark:text-slate-400 cursor-not-allowed border-stone-200 dark:border-nuguard-edge'
                      : showEmailError
                        ? 'border-rose-400 dark:border-rose-500'
                        : 'border-stone-200 dark:border-nuguard-edge'
                  )}
                  placeholder="name@company.com"
                  value={email}
                  onBlur={() => setEmailTouched(true)}
                  onChange={(e) => {
                    if (isInvite || isReset) return;
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                />
              </div>
              {showEmailError && (
                <p id="email-error" className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                  {emailError}
                </p>
              )}
            </div>

            {/* Password / New Password Field — hidden for forgot-password */}
            {mode !== 'forgot-password' && (
              <div>
                <label htmlFor="password" className="ng-auth-form-label">
                  {mode === 'reset-password' ? 'New Password' : 'Password'}
                </label>
                <div className="relative">
                  <div className="ng-input-icon-left">
                    <Lock className="h-5 w-5 text-stone-400 dark:text-slate-400" />
                  </div>
                  <input
                    id="password"
                    ref={passwordInputRef}
                    type={showPassword ? 'text' : 'password'}
                    required
                    autoComplete={mode === 'reset-password' ? 'new-password' : 'current-password'}
                    aria-invalid={showPasswordError}
                    aria-describedby={showPasswordError ? 'password-error' : undefined}
                    className={cn(
                      "ng-auth-input pl-10 pr-11 py-2",
                      showPasswordError
                        ? 'border-rose-400 dark:border-rose-500'
                        : 'border-stone-200 dark:border-nuguard-edge'
                    )}
                    placeholder="••••••••"
                    value={password}
                    onBlur={() => setPasswordTouched(true)}
                    onChange={(e) => {
                      setPassword(e.target.value);
                      if (error) setError('');
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(prev => !prev)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200"
                    aria-label={showPassword ? 'Hide password' : 'Show password'}
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {showPasswordError && (
                  <p id="password-error" className="mt-2 text-xs text-rose-600 dark:text-rose-400">
                    {passwordError}
                  </p>
                )}
              </div>
            )}

            {/* Confirm Password Field — reset-password only */}
            {mode === 'reset-password' && (
              <div>
                <label htmlFor="confirm-password" className="ng-auth-form-label">Confirm New Password</label>
                <div className="relative">
                  <div className="ng-input-icon-left">
                    <Lock className="h-5 w-5 text-stone-400 dark:text-slate-400" />
                  </div>
                  <input
                    id="confirm-password"
                    type={showConfirmPassword ? 'text' : 'password'}
                    required
                    autoComplete="new-password"
                    className={cn(
                      "ng-auth-input pl-10 pr-11 py-2",
                      submitted && !!confirmPasswordError
                        ? 'border-rose-400 dark:border-rose-500'
                        : 'border-stone-200 dark:border-nuguard-edge'
                    )}
                    placeholder="••••••••"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(prev => !prev)}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-stone-500 dark:text-slate-400 hover:text-stone-700 dark:hover:text-slate-200"
                    aria-label={showConfirmPassword ? 'Hide password' : 'Show password'}
                  >
                    {showConfirmPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
                {submitted && !!confirmPasswordError && (
                  <p className="mt-2 text-xs text-rose-600 dark:text-rose-400">{confirmPasswordError}</p>
                )}
              </div>
            )}

            {/* Forgot password link — login mode only */}
            {mode === 'login' && (
              <div className="flex justify-end -mt-1">
                <button
                  type="button"
                  onClick={() => switchMode('forgot-password')}
                  className="text-xs text-primary-600 dark:text-primary-400 hover:underline"
                >
                  Forgot password?
                </button>
              </div>
            )}

            {/* Escape hatch — invite link resolved to Sign In but this may be a brand-new
                user with no working password yet. Tabs are hidden for invite links, so
                without this they'd have no way to reach the account-creation form. */}
            {isInvite && mode === 'login' && (
              <p className="text-center text-xs text-stone-500 dark:text-slate-400 -mt-1">
                Don&apos;t have a password yet?{' '}
                <button
                  type="button"
                  onClick={() => switchMode('signup')}
                  className="text-primary-600 dark:text-primary-400 hover:underline font-medium"
                >
                  Create your account
                </button>
              </p>
            )}

            {/* Org Name Field — signup only, optional (locked when from invite or reset) */}
            {(mode === 'signup' || mode === 'reset-password') && (
              <div>
                <label htmlFor="org-name" className="ng-auth-form-label">
                  Organization Name{!isInvite && !isReset && <span className="text-stone-400 font-normal"> (optional)</span>}
                </label>
                <div className="relative">
                  <div className="ng-input-icon-left">
                    <Building2 className="h-5 w-5 text-stone-400 dark:text-slate-400" />
                  </div>
                  <input
                    id="org-name"
                    type="text"
                    autoComplete="organization"
                    readOnly={isInvite || isReset}
                    className={cn(
                      "ng-auth-input pl-10 pr-3 py-2 border-stone-200 dark:border-nuguard-edge",
                      (isInvite || isReset) && 'bg-stone-50 dark:bg-white/[0.04] text-stone-500 dark:text-slate-400 cursor-not-allowed'
                    )}
                    placeholder="Acme Corp"
                    value={orgName}
                    onChange={(e) => { if (!isInvite && !isReset) setOrgName(e.target.value); }}
                  />
                </div>
              </div>
            )}

            {/* Terms of Service — hidden until /docs/terms and /docs/privacy are implemented */}

            {/* Error Message */}
            {error && (
              <div
                role="alert"
                aria-live="polite"
                className="ng-banner-danger-compact text-center"
              >
                {error}
              </div>
            )}

            {/* Submit Button */}
            <button
              type="submit"
              data-testid="auth-submit"
              disabled={loading || !isFormValid}
              className={cn(
                "ng-btn-base ng-btn-primary w-full gap-2 py-2 px-4 font-semibold",
                "focus:ring-offset-2"
              )}
            >
              {loading ? (
                <>
                  <Loader2 className="w-5 h-5 animate-spin" />
                  {mode === 'signup' ? 'Creating Account...' : mode === 'forgot-password' ? 'Sending...' : mode === 'reset-password' ? 'Updating Password...' : 'Signing In...'}
                </>
              ) : (
                <>
                  {mode === 'signup' ? 'Create Account' : mode === 'forgot-password' ? 'Send Reset Link' : mode === 'reset-password' ? 'Update Password' : 'Sign In'}
                  <ArrowRight className="w-4 h-4" />
                </>
              )}
            </button>

          </form>

          {/* Back to sign in — forgot-password / reset-password modes */}
          {(mode === 'forgot-password' || mode === 'reset-password') && (
            <div className="mt-4 text-center">
              <button
                type="button"
                onClick={() => switchMode('login')}
                className="text-sm text-primary-600 dark:text-primary-400 hover:underline"
              >
                Back to sign in
              </button>
            </div>
          )}

          {/* Social sign-in — login and signup modes only */}
          {(mode === 'login' || mode === 'signup') && <div className="mt-5">
            <div className="relative mb-4">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200 dark:border-nuguard-edge" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-nuguard-card text-stone-500 dark:text-slate-400">
                  Or continue with
                </span>
              </div>
            </div>
            <div className="flex justify-center gap-4">
              {/* GitHub */}
              <button
                type="button"
                title="Sign in with GitHub"
                onClick={() => { if (postLoginRedirect) sessionStorage.setItem('post_login_redirect', postLoginRedirect); window.location.href = `${CONFIG.DATA_SERVICE_URL}/auth/oauth/github/authorize`; }}
                className="ng-auth-social-icon-btn !bg-[#24292f] dark:!bg-[#24292f] !border-[#24292f] hover:!bg-[#3d444d] dark:hover:!bg-[#3d444d] text-white"
              >
                <svg className="w-5 h-5" fill="white" viewBox="0 0 24 24">
                  <path fillRule="evenodd" d="M12 2C6.477 2 2 6.484 2 12.017c0 4.425 2.865 8.18 6.839 9.504.5.092.682-.217.682-.483 0-.237-.008-.868-.013-1.703-2.782.605-3.369-1.343-3.369-1.343-.454-1.158-1.11-1.466-1.11-1.466-.908-.62.069-.608.069-.608 1.003.07 1.531 1.032 1.531 1.032.892 1.53 2.341 1.088 2.91.832.092-.647.35-1.088.636-1.338-2.22-.253-4.555-1.113-4.555-4.951 0-1.093.39-1.988 1.029-2.688-.103-.253-.446-1.272.098-2.65 0 0 .84-.27 2.75 1.026A9.564 9.564 0 0112 6.844c.85.004 1.705.115 2.504.337 1.909-1.296 2.747-1.027 2.747-1.027.546 1.379.202 2.398.1 2.651.64.7 1.028 1.595 1.028 2.688 0 3.848-2.339 4.695-4.566 4.943.359.309.678.92.678 1.855 0 1.338-.012 2.419-.012 2.747 0 .268.18.58.688.482A10.019 10.019 0 0022 12.017C22 6.484 17.522 2 12 2z" clipRule="evenodd" />
                </svg>
              </button>
              {/* Google — commented out, uncomment to re-enable
              <button
                type="button"
                title="Sign in with Google"
                onClick={() => { if (postLoginRedirect) sessionStorage.setItem('post_login_redirect', postLoginRedirect); window.location.href = `${CONFIG.DATA_SERVICE_URL}/auth/oauth/google/authorize`; }}
                className="ng-auth-social-icon-btn"
              >
                <svg viewBox="0 0 24 24" className="w-5 h-5">
                  <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                  <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                  <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l3.66-2.84z" fill="#FBBC05" />
                  <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                </svg>
              </button>
              */}
              {/* Microsoft — commented out, uncomment to re-enable
              <button
                type="button"
                title="Sign in with Microsoft"
                onClick={() => { if (postLoginRedirect) sessionStorage.setItem('post_login_redirect', postLoginRedirect); window.location.href = `${CONFIG.DATA_SERVICE_URL}/auth/oauth/microsoft/authorize`; }}
                className="ng-auth-social-icon-btn"
              >
                <svg viewBox="0 0 23 23" className="w-5 h-5">
                  <path fill="#F35325" d="M0 0h11v11H0z" />
                  <path fill="#81BC06" d="M12 0h11v11H12z" />
                  <path fill="#05A6F0" d="M0 12h11v11H0z" />
                  <path fill="#FFBA08" d="M12 12h11v11H12z" />
                </svg>
              </button>
              */}
            </div>
          </div>}

          {/* Demo credentials — login mode only, dev environment only */}
          {mode === 'login' && CONFIG.ENABLE_DEMO_CREDENTIALS && <div className="mt-5">
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <div className="w-full border-t border-stone-200 dark:border-nuguard-edge" />
              </div>
              <div className="relative flex justify-center text-sm">
                <span className="px-2 bg-white dark:bg-nuguard-card text-stone-500 dark:text-slate-500">
                  or continue with Demo Credentials
                </span>
              </div>
            </div>

            {/* Demo User Buttons */}
            <div className="mt-4 grid grid-cols-3 gap-3">
              <button 
                type="button"
                onClick={() => loginWithDemo(DEMO_ADMIN_EMAIL, DEMO_ADMIN_PASSWORD)}
                className={cn(
                  "flex flex-col items-center justify-center px-2 py-3",
                  "border border-stone-200 dark:border-nuguard-edge rounded-lg shadow-sm",
                  "bg-white dark:bg-nuguard-card",
                  "text-sm font-medium text-stone-700 dark:text-slate-200",
                  "hover:bg-stone-50 dark:hover:bg-white/[0.06] hover:border-primary-300 dark:hover:border-primary-600",
                  "transition-colors"
                )}
              >
                <span className="font-bold text-stone-900 dark:text-white">Admin</span>
                <span className="ng-text-muted-xs">Full Access</span>
              </button>
              <button 
                type="button"
                onClick={() => loginWithDemo(DEMO_EDITOR_EMAIL, DEMO_EDITOR_PASSWORD)}
                className={cn(
                  "flex flex-col items-center justify-center px-2 py-3",
                  "border border-stone-200 dark:border-nuguard-edge rounded-lg shadow-sm",
                  "bg-white dark:bg-nuguard-card",
                  "text-sm font-medium text-stone-700 dark:text-slate-200",
                  "hover:bg-stone-50 dark:hover:bg-white/[0.06] hover:border-primary-300 dark:hover:border-primary-600",
                  "transition-colors"
                )}
              >
                <span className="font-bold text-stone-900 dark:text-white">Editor</span>
                <span className="ng-text-muted-xs">Edit Policies</span>
              </button>
              <button 
                type="button"
                onClick={() => loginWithDemo(DEMO_VIEWER_EMAIL, DEMO_VIEWER_PASSWORD)}
                className={cn(
                  "flex flex-col items-center justify-center px-2 py-3",
                  "border border-stone-200 dark:border-nuguard-edge rounded-lg shadow-sm",
                  "bg-white dark:bg-nuguard-card",
                  "text-sm font-medium text-stone-700 dark:text-slate-200",
                  "hover:bg-stone-50 dark:hover:bg-white/[0.06] hover:border-primary-300 dark:hover:border-primary-600",
                  "transition-colors"
                )}
              >
                <span className="font-bold text-stone-900 dark:text-white">Viewer</span>
                <span className="ng-text-muted-xs">Read Only</span>
              </button>
            </div>
          </div>}
        </div>

        <p className="text-center text-sm text-stone-600 dark:text-slate-400 mt-4">
          New to NuGuard?{' '}
          <a
            href="/docs/index.html#/quick-start"
            target="_blank"
            rel="noopener noreferrer"
            className="font-medium text-primary-600 hover:text-primary-700 dark:text-primary-500 dark:hover:text-primary-400"
          >
            Open Customer Quick Start
          </a>
        </p>

        {/* Footer */}
        <p className="text-center ng-text-muted-sm mt-6">
          &copy; 2026 NuGuard AI. AI Trust Platform.
        </p>
      </div>
    </div>
  );
};

export default Login;
